import { Fragment, FunctionComponent, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  formatAsCurrency,
  CurrencySymbols,
  Box,
  AccountStatus,
  GetColorStatus,
  withStyles,
  Pagination,
  AccountBalance,
  AccountParentAccountInfo,
  useUpdateQueryParams,
  Toast,
} from 'vopay';
import { TableFiltersContainer, TransactionStatus, Table as UITable, Button } from 'UI';
import Table, { createTableProps, Row } from '@/components/Accounts/Table/Table';
import AccountsSearchForm from './AccountsSearchForm';
import useFetchPartnerAccounts, { PartnerAccountStatus } from '@/hooks/useApi/useFetchPartnerAccounts';
import useFileDownload from '@/hooks/useFiledDownload';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useTranslation } from 'react-i18next';
import styles from './Accounts.module.scss';

const cn = withStyles(styles);

type BreadcrumbsProps = {
  parentAccounts: AccountParentAccountInfo[];
  onClick: (value: string) => void;
};

function Breadcrumbs({ parentAccounts, onClick }: BreadcrumbsProps) {
  const [expanded, setExpanded] = useState(false);

  const buildTree = (): string[] => {
    if (parentAccounts.length === 0) return [];
    const root = parentAccounts.find((acc) => acc.ParentAccountID === null);
    if (!root) return [];

    const chain: string[] = [root.AccountName];
    let current = parentAccounts.find((acc) => acc.ParentAccountID === root.AccountID);
    while (current) {
      chain.push(current.AccountName);
      current = parentAccounts.find((acc) => acc.ParentAccountID === current!.AccountID);
    }
    return chain;
  };

  const tree = buildTree();
  if (tree.length === 0) return null;

  const renderBreadcrumbItem = (item: string, index: number) => (
    <Fragment key={item}>
      <button type="button" onClick={() => onClick(item)}>
        {item}
      </button>
      {index < tree.length - 1 && ' > '}
    </Fragment>
  );

  return (
    <nav aria-label="Breadcrumbs">
      <span className={cn('breadcrumbs')}>
        {tree.length <= 2 || expanded ? (
          tree.map(renderBreadcrumbItem)
        ) : (
          <>
            {renderBreadcrumbItem(tree[0], 0)}
            <button type="button" onClick={() => setExpanded(true)}>
              ...
            </button>
            {' > '}
            {renderBreadcrumbItem(tree[tree.length - 1], tree.length - 1)}
          </>
        )}
      </span>
    </nav>
  );
}

type AccountsProps = {
  token: string;
};

type QueryParams = {
  country: string;
  status: string;
  searchText: string;
  currency: string;
  page: string;
};

const PAGE_SIZE = 20;

const Accounts: FunctionComponent<AccountsProps> = ({ token }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Partners.Details.Accounts' });
  const location = useLocation();
  const [fetchPartnerAccountsResponse, refreshPartnerAccounts] = useFetchPartnerAccounts({ bearer: token });
  const [exportResponse, exportPartnerAccounts] = useFetchPartnerAccounts({ bearer: token });
  const { downloadCsvFile } = useFileDownload();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const updateQueryParams = useUpdateQueryParams<QueryParams>();
  const [showExportToast, setShowExportToast] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const country = useMemo(() => queryParams.get('country') || '', [queryParams]);
  const status = useMemo(() => (queryParams.get('status') as PartnerAccountStatus) || '', [queryParams]);
  const currency = useMemo(() => queryParams.get('currency') || '', [queryParams]);
  const searchText = useMemo(() => queryParams.get('searchText') || '', [queryParams]);
  const pageIndex = useMemo(() => Number(queryParams.get('page')) || 0, [queryParams]);

  const hasSearch = useMemo(
    () => status !== '' || country !== '' || currency !== '' || searchText !== '',
    [status, country, currency, searchText],
  );

  const filtersApplied = useMemo(
    () => !!country || !!status || !!currency || !!searchText,
    [country, status, currency, searchText],
  );

  const totalRecords = fetchPartnerAccountsResponse?.PaginationMetaData?.TotalRecords || 0;
  const pageCount = useMemo(() => Math.ceil(totalRecords / PAGE_SIZE) || 0, [totalRecords]);
  const offset = pageIndex * PAGE_SIZE;

  useEffect(() => {
    if (!token) return;
    refreshPartnerAccounts({
      Country: country || '',
      VopayAccountStatus: status || '',
      SearchText: searchText || '',
      Currency: currency || '',
      Offset: offset,
    });
  }, [token, country, status, searchText, currency, offset]);

  useEffect(() => {
    if (exportResponse?.success && exportResponse.CSVFile) {
      downloadCsvFile(exportResponse.CSVFile, 'accounts');
      setShowExportToast(false);
    }
  }, [exportResponse]);

  const handleExportCSV = () => {
    setShowExportToast(true);
    exportPartnerAccounts({
      ExportCSV: true,
      Country: country,
      Currency: currency,
      VopayAccountStatus: status,
      SearchText: searchText,
    });
  };

  const balances = fetchPartnerAccountsResponse?.success ? fetchPartnerAccountsResponse.balances : [];

  function mapNestedAccounts(nestedAccounts: AccountBalance[]): Row<AccountBalance>[] {
    return nestedAccounts.map((account) => ({
      id: `${account.AccountID}-${account.Currency}`,
      data: account,
      subRows: (account.SubAccounts || []).length > 0 ? mapNestedAccounts(account.SubAccounts || []) : undefined,
    }));
  }

  function mapFlatAccounts(nestedAccounts: AccountBalance[]): Row<AccountBalance>[] {
    return nestedAccounts.map((account) => ({
      id: `${account.AccountID}-${account.Currency}`,
      data: account,
      subRows: undefined,
    }));
  }

  const accountLink = (data: AccountBalance) => ({
    pathname: data.ParentAccountName ? `/accounts/sub-account/${data.AccountID}` : `/accounts/${data.AccountID}`,
    search: new URLSearchParams({
      accountId: data.AccountID,
      isActive: String(data.IsActive),
      accountStatus: data.AccountStatus,
      currency: data.Currency,
      pending: data.PendingBalance,
      available: data.AvailableBalance,
      account: data.AccountBalance,
      reserve: data.Reserve,
      immediate: data.AvailableImmediately,
    }).toString(),
  });

  const statusColumn = {
    id: 'status',
    header: t('Table.status', { defaultValue: 'Status' }),
    align: 'left' as const,
    template: ({ data }: { data: AccountBalance }) =>
      data.IsActive ? (
        <TransactionStatus label={AccountStatus.Active} status="positive" />
      ) : (
        <TransactionStatus label={data.AccountStatus} status={GetColorStatus(data?.AccountStatus || '')} />
      ),
  };

  const countryColumn = {
    id: 'country',
    header: t('Table.country', { defaultValue: 'Country' }),
    field: 'Country' as const,
    align: 'left' as const,
  };

  const pendingColumn = {
    id: 'pending',
    header: t('Table.pending', { defaultValue: 'Pending Balance' }),
    align: 'left' as const,
    template: ({ data }: { data: AccountBalance }) =>
      `${data.Currency} ${formatAsCurrency(data.PendingBalance, CurrencySymbols[data.Currency as keyof typeof CurrencySymbols])}`,
  };

  const availableColumn = {
    id: 'available',
    header: t('Table.available', { defaultValue: 'Available Balance' }),
    align: 'left' as const,
    template: ({ data }: { data: AccountBalance }) =>
      `${data.Currency} ${formatAsCurrency(data.AvailableBalance, CurrencySymbols[data.Currency as keyof typeof CurrencySymbols])}`,
  };

  const tableProps = createTableProps({
    id: 'partner-accounts-list',
    hasPlaceholderArrows: true,
    rows: mapNestedAccounts(balances),
    columns: [
      {
        id: 'accountName',
        header: t('Table.accountName', { defaultValue: 'Account Name' }),
        align: 'left',
        template: ({ data }: { data: AccountBalance }) => (
          <div style={{ minWidth: '300px' }}>
            <UITable.Link to={accountLink(data)}>{data.AccountName}</UITable.Link>
            {data.DBAName && (
              <>
                <br />
                <i>{data.DBAName}</i>
              </>
            )}
          </div>
        ),
      },
      countryColumn,
      pendingColumn,
      availableColumn,
      { ...statusColumn, hasDivider: false },
    ],
  });

  const searchTableProps = createTableProps({
    id: 'partner-accounts-list',
    hasPlaceholderArrows: false,
    rows: mapFlatAccounts(balances),
    columns: [
      {
        id: 'accountName',
        header: t('Table.accountName', { defaultValue: 'Account Name' }),
        align: 'left',
        template: ({ data }: { data: AccountBalance }) => {
          const onClick = (value: string) => {
            updateQueryParams({ searchText: value, country: '', currency: '', status: '', page: '' });
          };
          return (
            <Box flx="col" ai="fs">
              <Breadcrumbs parentAccounts={data.ParentAccounts} onClick={onClick} />
              <UITable.Link to={accountLink(data)}>{data.AccountName}</UITable.Link>
              {data.DBAName && <i>{data.DBAName}</i>}
            </Box>
          );
        },
      },
      countryColumn,
      pendingColumn,
      availableColumn,
      statusColumn,
    ],
  });

  return (
    <div className={cn('accounts')}>
      <TableFiltersContainer filtersApplied={filtersApplied} open={false}>
        <div className={cn('filtersHeader')}>
          <TableFiltersContainer.Trigger />
          <Button
            id="partner-accounts-ExportCSV"
            type="button"
            colorScheme="secondary"
            size="small"
            disabled={isRoleViewOnly}
            onClick={handleExportCSV}
          >
            {t('exportCSVButton', { defaultValue: 'Export CSV' })}
          </Button>
        </div>
        <TableFiltersContainer.Content>
          <AccountsSearchForm
            country={country}
            status={status}
            searchText={searchText}
            currency={currency}
            onAccountSearch={(values) => {
              updateQueryParams({
                country: values.Country || '',
                status: values.VopayAccountStatus || '',
                searchText: values.SearchText || '',
                currency: values.Currency || '',
                page: '',
              });
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <Box className={cn('tableContainer', 'scrollbar')}>
        {hasSearch ? <Table {...searchTableProps} /> : <Table {...tableProps} />}
      </Box>

      {pageCount > 1 && (
        <Box flx="row" jc="center" mt="md" base="8">
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => updateQueryParams({ page: e.selected ? String(e.selected) : '' })}
          />
        </Box>
      )}

      {createPortal(
        <Toast
          isVisible={showExportToast}
          label={t('exportCSVToast', {
            defaultValue: 'Your CSV export is being prepared. Large account lists may take a few minutes to generate.',
          })}
          variant="neutral"
          onClose={() => setShowExportToast(false)}
        />,
        document.body,
      )}
    </div>
  );
};

export default Accounts;
